import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Search, MapPin, DollarSign, Calendar, X, Briefcase, Music2, Building2, Users as UsersIcon, PartyPopper, Share2 } from 'lucide-react';
import ShareToWallModal from '../ShareToWallModal';

const JobBoardTab = ({ onBack }) => {
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flippedCard, setFlippedCard] = useState(null);
  const [selectedGig, setSelectedGig] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [wallShareGig, setWallShareGig] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [venueTypeFilter, setVenueTypeFilter] = useState('all');
  const [genreFilter, setGenreFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');

  // Post Gig Form State
  const [postForm, setPostForm] = useState({
    title: '',
    venue_name: '',
    venue_type: 'nightclub',
    genre: 'house',
    location: '',
    event_date: '',
    pay_range: '$500-$1000',
    summary: '',
    description: '',
    requirements: '',
    perks: '',
    contact_email: '',
    contact_phone: '',
    is_hot: false
  });
  const [postError, setPostError] = useState('');
  const [postSuccess, setPostSuccess] = useState(false);

  // Load gigs on mount and when filters change
  useEffect(() => {
    loadGigs();
  }, [venueTypeFilter, genreFilter, locationFilter, sortBy]);

  const loadGigs = async () => {
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams();
      
      if (venueTypeFilter !== 'all') params.append('venue_type', venueTypeFilter);
      if (genreFilter !== 'all') params.append('genre', genreFilter);
      if (locationFilter !== 'all') params.append('location', locationFilter);
      params.append('sort_by', sortBy);
      
      const response = await fetch(`${BACKEND_URL}/api/gigs?${params}`);
      if (response.ok) {
        const data = await response.json();
        setGigs(data);
      }
    } catch (error) {
      console.error('Failed to load gigs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get venue type icon and color
  const getVenueStyle = (venueType) => {
    const styles = {
      'festival': { color: '#FFD700', icon: PartyPopper, label: '🎪 Festival' },
      'nightclub': { color: '#9D4EDD', icon: Music2, label: '🏙️ Nightclub' },
      'mobile-party': { color: '#FF006E', icon: UsersIcon, label: '💒 Mobile Party' },
      'corporate-event': { color: '#3A86FF', icon: Building2, label: '🏢 Corporate' }
    };
    return styles[venueType] || { color: '#00ffff', icon: Briefcase, label: venueType };
  };

  // Filter gigs by search term
  const filteredGigs = gigs.filter(gig => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      gig.title.toLowerCase().includes(searchLower) ||
      gig.venue_name.toLowerCase().includes(searchLower) ||
      gig.location.toLowerCase().includes(searchLower) ||
      gig.genre.toLowerCase().includes(searchLower)
    );
  });

  const handleCardClick = (gigId) => {
    setFlippedCard(flippedCard === gigId ? null : gigId);
  };

  const openFullDetails = (gig) => {
    setSelectedGig(gig);
    setShowModal(true);
    setFlippedCard(null);
  };

  const handlePostFormChange = (field, value) => {
    setPostForm(prev => ({ ...prev, [field]: value }));
    setPostError('');
  };

  const handleSubmitGig = async () => {
    // Validation
    if (!postForm.title.trim()) {
      setPostError('Job title is required');
      return;
    }
    if (!postForm.venue_name.trim()) {
      setPostError('Venue/Company name is required');
      return;
    }
    if (!postForm.location.trim()) {
      setPostError('Location is required');
      return;
    }
    if (!postForm.summary.trim()) {
      setPostError('Short summary is required');
      return;
    }
    if (postForm.summary.length > 200) {
      setPostError('Summary must be 200 characters or less');
      return;
    }
    if (!postForm.description.trim()) {
      setPostError('Full description is required');
      return;
    }
    if (!postForm.contact_email.trim()) {
      setPostError('Contact email is required');
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      if (!token) {
        setPostError('You must be logged in to post a gig');
        return;
      }

      // Parse requirements and perks from comma-separated strings
      const requirements = postForm.requirements
        .split(',')
        .map(r => r.trim())
        .filter(r => r.length > 0);
      
      const perks = postForm.perks
        .split(',')
        .map(p => p.trim())
        .filter(p => p.length > 0);

      const payload = {
        title: postForm.title,
        venue_name: postForm.venue_name,
        venue_type: postForm.venue_type,
        genre: postForm.genre,
        location: postForm.location,
        event_date: postForm.event_date || null,
        pay_range: postForm.pay_range,
        summary: postForm.summary,
        description: postForm.description,
        requirements,
        perks,
        contact_email: postForm.contact_email,
        contact_phone: postForm.contact_phone || null,
        is_hot: postForm.is_hot
      };

      const response = await fetch(`${BACKEND_URL}/api/gigs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to post gig');
      }

      // Success!
      setPostSuccess(true);
      setTimeout(() => {
        setShowPostModal(false);
        setPostSuccess(false);
        setPostForm({
          title: '',
          venue_name: '',
          venue_type: 'nightclub',
          genre: 'house',
          location: '',
          event_date: '',
          pay_range: '$500-$1000',
          summary: '',
          description: '',
          requirements: '',
          perks: '',
          contact_email: '',
          contact_phone: '',
          is_hot: false
        });
        loadGigs(); // Reload gigs
      }, 2000);
    } catch (error) {
      setPostError(error.message);
    }
  };

  return (
    <div className="job-board-tab" style={{
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 220px)',
      maxHeight: '75vh',
      padding: '0 2rem 2rem 2rem'
    }}>
      {/* Header - Horizontal Layout with Post Text */}
      <div className="job-board-header" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginTop: '-1rem',
        marginBottom: '0.5rem',
        paddingLeft: '0'
      }}>
        <div className="job-board-title-block" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            margin: 0,
            whiteSpace: 'nowrap'
          }}>
            💼 Job Board
          </h2>
          <p style={{
            fontSize: '0.95rem',
            color: 'rgba(255, 255, 255, 0.7)',
            margin: 0
          }}>
            Find your next gig opportunity
          </p>
        </div>

        {/* Post a Gig - Simple Text */}
        <span
          onClick={() => setShowPostModal(true)}
          style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: '0.95rem',
            cursor: 'pointer',
            transition: 'color 0.2s ease',
            whiteSpace: 'nowrap'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#00ffff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
          }}
        >
          ✨ Post a Gig
        </span>
      </div>

      {/* Back Link */}
      <div
        onClick={onBack}
        style={{
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '0.85rem',
          cursor: 'pointer',
          transition: 'color 0.2s ease',
          textDecoration: 'underline',
          marginBottom: '0.75rem'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#00ffff';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
        }}
      >
        ← Back to Gig & Event Hub
      </div>

      {/* Filter Bar */}
      <div className="job-board-filters" style={{
        background: 'rgba(0, 0, 0, 0.3)',
        borderRadius: '12px',
        padding: '0.75rem',
        marginBottom: '0.75rem',
        display: 'flex',
        gap: '1rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Search */}
        <div style={{ flex: '1 1 250px', position: 'relative' }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'rgba(255, 255, 255, 0.5)'
          }} />
          <input
            type="text"
            placeholder="Search gigs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 0.75rem 0.75rem 2.5rem',
              background: 'rgba(0, 0, 0, 0.4)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '0.95rem'
            }}
          />
        </div>

        {/* Venue Type */}
        <select
          value={venueTypeFilter}
          onChange={(e) => setVenueTypeFilter(e.target.value)}
          style={{
            padding: '0.75rem',
            background: 'rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '0.95rem',
            cursor: 'pointer'
          }}
        >
          <option value="all">Gig Types</option>
          <option value="festival">🎪 Festival</option>
          <option value="nightclub">🏙️ Nightclub</option>
          <option value="mobile-party">💒 Mobile Party</option>
          <option value="corporate-event">🏢 Corporate</option>
        </select>

        {/* Genre */}
        <select
          value={genreFilter}
          onChange={(e) => setGenreFilter(e.target.value)}
          style={{
            padding: '0.75rem',
            background: 'rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '0.95rem',
            cursor: 'pointer'
          }}
        >
          <option value="all">Genres</option>
          <option value="house">House</option>
          <option value="techno">Techno</option>
          <option value="hip-hop">Hip Hop</option>
          <option value="edm">EDM</option>
          <option value="open-format">Open Format</option>
        </select>

        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            padding: '0.75rem',
            background: 'rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            color: '#fff',
            fontSize: '0.95rem',
            cursor: 'pointer'
          }}
        >
          <option value="created_at">Newest First</option>
          <option value="pay">Highest Pay</option>
          <option value="event_date">Event Date</option>
        </select>
      </div>

      {/* Gig Cards - Family Feud Style */}
      <div className="job-board-cards" style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.6)' }}>
            Loading gigs...
          </div>
        ) : filteredGigs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.6)' }}>
            No gigs found matching your criteria
          </div>
        ) : (
          filteredGigs.map((gig) => {
            const venueStyle = getVenueStyle(gig.venue_type);
            const isFlipped = flippedCard === gig.id;

            return (
              <div
                key={gig.id}
                className="job-card"
                onClick={() => handleCardClick(gig.id)}
                style={{
                  position: 'relative',
                  height: '90px',
                  perspective: '1000px',
                  cursor: 'pointer'
                }}
              >
                <div style={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  transformStyle: 'preserve-3d',
                  transition: 'transform 0.6s',
                  transform: isFlipped ? 'rotateX(180deg)' : 'rotateX(0deg)'
                }}>
                  {/* Front of Card */}
                  <div 
                    className={`job-card-front-${gig.id}`}
                    style={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      backfaceVisibility: 'hidden',
                      background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(255, 255, 255, 0.05))',
                      border: '2px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.background = `linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(${venueStyle.color.replace('#', '')}, 0.1))`;
                      e.currentTarget.style.borderColor = venueStyle.color;
                      e.currentTarget.style.boxShadow = `0 12px 48px rgba(${venueStyle.color.replace('#', '')}, 0.5)`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(255, 255, 255, 0.05))';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                      e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
                    }}
                  >
                    <div style={{ 
                      flex: 1, 
                      display: 'flex', 
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: '0.5rem'
                    }}>
                      {/* Top Row: Title and Info */}
                      <div style={{
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '2rem'
                      }}>
                        {/* Left: Title */}
                        <h3 style={{
                          fontSize: '1.1rem',
                          fontWeight: '700',
                          color: '#fff',
                          margin: 0,
                          flex: '0 0 auto',
                          maxWidth: '35%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {gig.title}
                        </h3>

                        {/* Right: Info Row */}
                        <div style={{ 
                          display: 'flex', 
                          gap: '2rem', 
                          alignItems: 'center',
                          flex: 1
                        }}>
                          <span style={{ 
                            color: venueStyle.color, 
                            fontSize: '0.95rem', 
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            {venueStyle.label}
                          </span>
                          
                          <span style={{ 
                            color: 'rgba(255, 255, 255, 0.8)', 
                            fontSize: '0.95rem', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.4rem',
                            whiteSpace: 'nowrap'
                          }}>
                            <DollarSign size={16} /> {gig.pay_range}
                          </span>
                          
                          <span style={{ 
                            color: 'rgba(255, 255, 255, 0.8)', 
                            fontSize: '0.95rem', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.4rem',
                            whiteSpace: 'nowrap'
                          }}>
                            <MapPin size={16} /> {gig.location}
                          </span>
                          
                          <span style={{ 
                            color: 'rgba(255, 255, 255, 0.8)', 
                            fontSize: '0.95rem', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.4rem',
                            whiteSpace: 'nowrap'
                          }}>
                            <Music2 size={16} /> {gig.genre}
                          </span>

                          {gig.is_hot && (
                            <span style={{
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              color: '#ff4757',
                              whiteSpace: 'nowrap'
                            }}>
                              🔥 HOT
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Bottom Row: Share + View Links */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1.25rem', alignItems: 'center' }}>
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            setWallShareGig(gig);
                          }}
                          title="Share to The Wall"
                          data-testid={`gig-wall-share-${gig.id}`}
                          style={{
                            color: '#22d3ee',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            transition: 'color 0.2s ease',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#fff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#22d3ee';
                          }}
                        >
                          <Share2 size={14} /> Wall
                        </span>
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCardClick(gig.id);
                          }}
                          style={{
                            color: venueStyle.color,
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            transition: 'color 0.2s ease',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#fff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = venueStyle.color;
                          }}
                        >
                          View
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Back of Card */}
                  <div style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    backfaceVisibility: 'hidden',
                    background: `linear-gradient(135deg, rgba(${venueStyle.color.replace('#', '')}, 0.2), rgba(0, 0, 0, 0.6))`,
                    border: `2px solid ${venueStyle.color}`,
                    borderRadius: '12px',
                    padding: '2rem',
                    transform: 'rotateX(180deg)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 8px 32px rgba(${venueStyle.color.replace('#', '')}, 0.3)`
                  }}>
                    <p style={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '1rem',
                      lineHeight: '1.5',
                      margin: '0 0 1.5rem 0',
                      textAlign: 'center',
                      flex: '0 0 auto'
                    }}>
                      {gig.summary}
                    </p>
                    
                    {/* Full Details Link - Centered at bottom */}
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        openFullDetails(gig);
                      }}
                      style={{
                        color: venueStyle.color,
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        transition: 'color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#fff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = venueStyle.color;
                      }}
                    >
                      Full Details
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Full Details Modal - Using Portal to render outside DJ Booth container */}
      {showModal && selectedGig && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: '1rem'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              width: '90%',
              maxWidth: '800px',
              maxHeight: '90vh',
              background: 'rgba(26, 26, 46, 0.98)',
              borderRadius: '16px',
              overflow: 'hidden',
              border: `3px solid ${getVenueStyle(selectedGig.venue_type).color}`,
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '2rem',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexShrink: 0
            }}>
              <div style={{ flex: 1 }}>
                <h2 style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: '#fff',
                  margin: '0 0 0.5rem 0'
                }}>
                  {selectedGig.title}
                </h2>
                <p style={{
                  fontSize: '1.25rem',
                  color: getVenueStyle(selectedGig.venue_type).color,
                  fontWeight: '600',
                  margin: '0.5rem 0'
                }}>
                  {selectedGig.venue_name}
                </p>
                <div style={{ display: 'flex', gap: '2rem', marginTop: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ color: 'rgba(255, 255, 255, 0.8)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {getVenueStyle(selectedGig.venue_type).label}
                  </span>
                  <span style={{ color: 'rgba(255, 255, 255, 0.8)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <DollarSign size={18} /> {selectedGig.pay_range}
                  </span>
                  <span style={{ color: 'rgba(255, 255, 255, 0.8)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <MapPin size={18} /> {selectedGig.location}
                  </span>
                  <span style={{ color: 'rgba(255, 255, 255, 0.8)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Music2 size={18} /> {selectedGig.genre}
                  </span>
                  {selectedGig.is_hot && (
                    <span style={{
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#ff4757',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      🔥 HOT
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'rgba(255, 0, 128, 0.2)',
                  border: '1px solid rgba(255, 0, 128, 0.4)',
                  color: '#ff0080',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Content */}
            <div 
              className="full-details-scrollable"
              style={{ 
                padding: '2rem', 
                overflowY: 'auto', 
                flex: 1,
                WebkitOverflowScrolling: 'touch'
              }}
            >
              <style>{`
                .full-details-scrollable::-webkit-scrollbar {
                  width: 8px;
                }
                .full-details-scrollable::-webkit-scrollbar-track {
                  background: rgba(0, 0, 0, 0.2);
                  border-radius: 4px;
                }
                .full-details-scrollable::-webkit-scrollbar-thumb {
                  background: rgba(0, 255, 255, 0.3);
                  border-radius: 4px;
                }
                .full-details-scrollable::-webkit-scrollbar-thumb:hover {
                  background: rgba(0, 255, 255, 0.5);
                }
              `}</style>
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: '#fff',
                  marginBottom: '1rem'
                }}>
                  Description
                </h3>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: '1.6',
                  fontSize: '1rem'
                }}>
                  {selectedGig.description}
                </p>
              </div>

              {selectedGig.requirements && selectedGig.requirements.length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    color: '#fff',
                    marginBottom: '1rem'
                  }}>
                    Requirements
                  </h3>
                  <ul style={{ padding: '0 0 0 1.5rem', margin: 0 }}>
                    {selectedGig.requirements.map((req, index) => (
                      <li key={index} style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        marginBottom: '0.5rem',
                        fontSize: '1rem'
                      }}>
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedGig.perks && selectedGig.perks.length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    color: '#fff',
                    marginBottom: '1rem'
                  }}>
                    Perks
                  </h3>
                  <ul style={{ padding: '0 0 0 1.5rem', margin: 0 }}>
                    {selectedGig.perks.map((perk, index) => (
                      <li key={index} style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        marginBottom: '0.5rem',
                        fontSize: '1rem'
                      }}>
                        ✨ {perk}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(selectedGig.contact_email || selectedGig.contact_phone) && (
                <div style={{
                  padding: '1.5rem',
                  background: 'rgba(0, 255, 255, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 255, 255, 0.3)'
                }}>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    color: '#fff',
                    marginBottom: '1rem'
                  }}>
                    Contact Information
                  </h3>
                  {selectedGig.contact_email && (
                    <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '0.5rem' }}>
                      📧 <a 
                        href={`mailto:${selectedGig.contact_email}`}
                        style={{
                          color: '#00ffff',
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#fff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#00ffff';
                        }}
                      >
                        {selectedGig.contact_email}
                      </a>
                    </p>
                  )}
                  {selectedGig.contact_phone && (
                    <p style={{ color: 'rgba(255, 255, 255, 0.8)', margin: 0 }}>
                      📞 {selectedGig.contact_phone}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Post a Gig Modal - Using Portal to render outside DJ Booth container */}
      {showPostModal && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: '1rem'
          }}
          onClick={() => setShowPostModal(false)}
        >
          <div
            style={{
              width: '90%',
              maxWidth: '600px',
              maxHeight: '90vh',
              background: 'rgba(26, 26, 46, 0.98)',
              borderRadius: '16px',
              overflow: 'hidden',
              border: '3px solid #00ffff',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '1.5rem 2rem',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <h2 style={{
                fontSize: '1.75rem',
                fontWeight: '700',
                color: '#fff',
                margin: 0,
                background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                ✨ Post a Gig
              </h2>
              <button
                onClick={() => setShowPostModal(false)}
                style={{
                  background: 'rgba(255, 0, 128, 0.2)',
                  border: '1px solid rgba(255, 0, 128, 0.4)',
                  color: '#ff0080',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Post Gig Form */}
            <div 
              className="post-gig-scrollable"
              style={{ 
                padding: '2rem', 
                overflowY: 'auto', 
                flex: 1,
                WebkitOverflowScrolling: 'touch'
              }}
            >
              <style>{`
                .post-gig-scrollable::-webkit-scrollbar {
                  width: 8px;
                }
                .post-gig-scrollable::-webkit-scrollbar-track {
                  background: rgba(0, 0, 0, 0.2);
                  border-radius: 4px;
                }
                .post-gig-scrollable::-webkit-scrollbar-thumb {
                  background: rgba(0, 255, 255, 0.3);
                  border-radius: 4px;
                }
                .post-gig-scrollable::-webkit-scrollbar-thumb:hover {
                  background: rgba(0, 255, 255, 0.5);
                }
              `}</style>
              {postSuccess ? (
                <div style={{
                  background: 'rgba(0, 255, 0, 0.1)',
                  border: '1px solid rgba(0, 255, 0, 0.3)',
                  borderRadius: '12px',
                  padding: '2rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: '#00ff00',
                    marginBottom: '1rem'
                  }}>
                    Gig Posted Successfully!
                  </h3>
                  <p style={{
                    fontSize: '1rem',
                    color: 'rgba(255, 255, 255, 0.8)',
                    margin: 0
                  }}>
                    Your gig listing is now live on the Job Board.
                  </p>
                </div>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); handleSubmitGig(); }}>
                  {/* Error Message */}
                  {postError && (
                    <div style={{
                      background: 'rgba(255, 0, 0, 0.1)',
                      border: '1px solid rgba(255, 0, 0, 0.3)',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1.5rem',
                      color: '#ff4444',
                      fontSize: '0.95rem'
                    }}>
                      {postError}
                    </div>
                  )}

                  {/* Job Title */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontWeight: '600' }}>
                      Job Title *
                    </label>
                    <input
                      type="text"
                      value={postForm.title}
                      onChange={(e) => handlePostFormChange('title', e.target.value)}
                      placeholder="e.g., Weekend Nightclub Resident DJ"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* Venue/Company Name */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontWeight: '600' }}>
                      Venue/Company Name *
                    </label>
                    <input
                      type="text"
                      value={postForm.venue_name}
                      onChange={(e) => handlePostFormChange('venue_name', e.target.value)}
                      placeholder="e.g., Club Noir"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* Row: Venue Type & Genre */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div>
                      <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontWeight: '600' }}>
                        Venue Type *
                      </label>
                      <select
                        value={postForm.venue_type}
                        onChange={(e) => handlePostFormChange('venue_type', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: 'rgba(0, 0, 0, 0.4)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '1rem',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="nightclub">🏙️ Nightclub</option>
                        <option value="festival">🎪 Festival</option>
                        <option value="mobile-party">💒 Mobile Party</option>
                        <option value="corporate-event">🏢 Corporate Event</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontWeight: '600' }}>
                        Genre *
                      </label>
                      <select
                        value={postForm.genre}
                        onChange={(e) => handlePostFormChange('genre', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: 'rgba(0, 0, 0, 0.4)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '1rem',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="house">House</option>
                        <option value="techno">Techno</option>
                        <option value="hip-hop">Hip Hop</option>
                        <option value="edm">EDM</option>
                        <option value="open-format">Open Format</option>
                      </select>
                    </div>
                  </div>

                  {/* Row: Location & Pay Range */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div>
                      <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontWeight: '600' }}>
                        Location *
                      </label>
                      <input
                        type="text"
                        value={postForm.location}
                        onChange={(e) => handlePostFormChange('location', e.target.value)}
                        placeholder="e.g., Los Angeles, CA"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: 'rgba(0, 0, 0, 0.4)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '1rem'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontWeight: '600' }}>
                        Pay Range *
                      </label>
                      <select
                        value={postForm.pay_range}
                        onChange={(e) => handlePostFormChange('pay_range', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: 'rgba(0, 0, 0, 0.4)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '1rem',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="$100-$500">$100-$500</option>
                        <option value="$500-$1000">$500-$1000</option>
                        <option value="$1000+">$1000+</option>
                        <option value="TBD">TBD</option>
                      </select>
                    </div>
                  </div>

                  {/* Event Date (Optional) */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontWeight: '600' }}>
                      Event Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={postForm.event_date}
                      onChange={(e) => handlePostFormChange('event_date', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* Short Summary (for card back) */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontWeight: '600' }}>
                      Short Summary (appears on card) *
                    </label>
                    <textarea
                      value={postForm.summary}
                      onChange={(e) => handlePostFormChange('summary', e.target.value)}
                      placeholder="Write a brief, attention-grabbing summary (2-3 sentences max)"
                      rows={3}
                      maxLength={200}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '1rem',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                    <div style={{
                      fontSize: '0.85rem',
                      color: postForm.summary.length > 200 ? '#ff4444' : 'rgba(255, 255, 255, 0.6)',
                      marginTop: '0.5rem',
                      textAlign: 'right'
                    }}>
                      {postForm.summary.length}/200 characters
                    </div>
                  </div>

                  {/* Full Description */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontWeight: '600' }}>
                      Full Description *
                    </label>
                    <textarea
                      value={postForm.description}
                      onChange={(e) => handlePostFormChange('description', e.target.value)}
                      placeholder="Provide detailed information about the gig, venue, expectations, and what makes this opportunity special..."
                      rows={5}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '1rem',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>

                  {/* Requirements */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontWeight: '600' }}>
                      Requirements (Optional)
                    </label>
                    <input
                      type="text"
                      value={postForm.requirements}
                      onChange={(e) => handlePostFormChange('requirements', e.target.value)}
                      placeholder="Separate with commas: 2+ years experience, Own equipment, etc."
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* Perks */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontWeight: '600' }}>
                      Perks (Optional)
                    </label>
                    <input
                      type="text"
                      value={postForm.perks}
                      onChange={(e) => handlePostFormChange('perks', e.target.value)}
                      placeholder="Separate with commas: Free drinks, Guest list, Networking, etc."
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* Contact Email */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontWeight: '600' }}>
                      Contact Email *
                    </label>
                    <input
                      type="email"
                      value={postForm.contact_email}
                      onChange={(e) => handlePostFormChange('contact_email', e.target.value)}
                      placeholder="contact@venue.com"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* Contact Phone (Optional) */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontWeight: '600' }}>
                      Contact Phone (Optional)
                    </label>
                    <input
                      type="tel"
                      value={postForm.contact_phone}
                      onChange={(e) => handlePostFormChange('contact_phone', e.target.value)}
                      placeholder="(555) 123-4567"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* Hot Gig Checkbox */}
                  <div style={{ marginBottom: '2rem' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      color: '#fff',
                      cursor: 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={postForm.is_hot}
                        onChange={(e) => handlePostFormChange('is_hot', e.target.checked)}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer'
                        }}
                      />
                      <span style={{ fontWeight: '600' }}>🔥 Mark as Hot/Urgent Gig</span>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    style={{
                      width: '100%',
                      padding: '1rem',
                      background: 'linear-gradient(135deg, #00ffff, #ff00ff)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    Post Gig to Job Board
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Share to The Wall */}
      {wallShareGig && ReactDOM.createPortal(
        <ShareToWallModal
          isOpen={!!wallShareGig}
          onClose={() => setWallShareGig(null)}
          source="gig"
          eventItem={{
            title: wallShareGig.title,
            subtitle: wallShareGig.venue_name,
            date_line: wallShareGig.event_date ? `📅 ${new Date(wallShareGig.event_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}` : null,
            location: wallShareGig.location,
            genre: wallShareGig.genre,
            extra: wallShareGig.pay_range ? `💰 ${wallShareGig.pay_range}` : null,
          }}
          currentUser={(() => { try { return JSON.parse(localStorage.getItem('beatseek_user') || 'null'); } catch { return null; } })()}
        />,
        document.body
      )}
    </div>
  );
};

export default JobBoardTab;
